<div class="ws-page ws-page-spacious">
  <header class="page-head">
    <div>
      <p class="kicker">Configuration · Administration</p>
      <div class="page-head-title-row">
        <app-page-back-btn />
        <h2>Metadata administration</h2>
        <app-page-refresh-btn (refresh)="reload()" />
      </div>
      <p class="muted ws-lead">Categories, sub-types and lookup codes for bye-law norms.</p>
    </div>
  </header>

  <p class="error" *ngIf="error()">{{ error() }}</p>

  <section class="page-card admin-tabs-card">
    <nav class="admin-tab-bar" role="tablist" aria-label="Administration sections">
      <button
        type="button"
        class="admin-tab"
        role="tab"
        [class.admin-tab-active]="activeTab() === 'hierarchy'"
        [attr.aria-selected]="activeTab() === 'hierarchy'"
        (click)="setTab('hierarchy')"
      >
        Use-type hierarchy
        <span class="admin-tab-count">{{ hierarchyTotal() }}</span>
      </button>
      <button
        type="button"
        class="admin-tab"
        role="tab"
        [class.admin-tab-active]="activeTab() === 'codes'"
        [attr.aria-selected]="activeTab() === 'codes'"
        (click)="setTab('codes')"
      >
        System code values
        <span class="admin-tab-count">{{ codeTotal() }}</span>
      </button>
    </nav>

    <!-- Tab: Use-type hierarchy -->
    <div class="admin-tab-panel" role="tabpanel" *ngIf="activeTab() === 'hierarchy'">
      <div class="admin-panel-toolbar">
        <label class="admin-filter-field">
          Level filter
          <select [(ngModel)]="levelFilter" (ngModelChange)="onLevelFilterChange()">
            <option value="category">category</option>
            <option value="subtype">subtype</option>
          </select>
        </label>
        <button type="button" class="btn primary btn-compact" (click)="openHierarchyModal()">
          + Add hierarchy node
        </button>
      </div>

      <p class="ws-loading" *ngIf="loadingHierarchy()">Loading hierarchy…</p>
      <div class="table-wrap" *ngIf="!loadingHierarchy() && hierarchy().length">
        <table class="data">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Level</th>
              <th>Parent</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let h of hierarchy()">
              <td class="text-meta">{{ h.code }}</td>
              <td>{{ h.name }}</td>
              <td>{{ h.level }}</td>
              <td>{{ h.parentCode || '—' }}</td>
              <td><app-status-badge [statusCd]="h.statusCd">{{ h.statusCd }}</app-status-badge></td>
              <td>
                <button type="button" class="btn secondary btn-compact" (click)="deactivateHierarchy(h)" *ngIf="h.statusCd === 'active'">
                  Deactivate
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="ws-empty" *ngIf="!loadingHierarchy() && !hierarchy().length">No hierarchy rows on this page.</p>

      <div class="pager" *ngIf="!loadingHierarchy() && hierarchyTotal() > 0">
        <span class="pager-meta">{{ hierarchyRangeLabel() }} · Page {{ hierarchyPage() }} / {{ hierarchyTotalPages() }}</span>
        <div class="actions">
          <button type="button" class="btn secondary" [disabled]="hierarchyPage() <= 1" (click)="goHierarchyFirst()">First</button>
          <button type="button" class="btn secondary" [disabled]="hierarchyPage() <= 1" (click)="goHierarchyPrev()">Previous</button>
          <button type="button" class="btn secondary" [disabled]="hierarchyPage() >= hierarchyTotalPages()" (click)="goHierarchyNext()">Next</button>
          <button type="button" class="btn secondary" [disabled]="hierarchyPage() >= hierarchyTotalPages()" (click)="goHierarchyLast()">Last</button>
        </div>
      </div>
    </div>

    <!-- Tab: System code values -->
    <div class="admin-tab-panel" role="tabpanel" *ngIf="activeTab() === 'codes'">
      <div class="admin-panel-toolbar admin-panel-toolbar--end">
        <button type="button" class="btn primary btn-compact" (click)="openCodeModal()">
          + Add code value
        </button>
      </div>

      <p class="ws-loading" *ngIf="loadingCodes()">Loading code values…</p>
      <div class="table-wrap" *ngIf="!loadingCodes() && codeValues().length">
        <table class="data">
          <thead>
            <tr>
              <th>Type</th>
              <th>Code</th>
              <th>Label</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let c of codeValues()">
              <td>{{ c.codeType }}</td>
              <td class="text-meta">{{ c.code }}</td>
              <td>{{ c.label }}</td>
              <td><app-status-badge [statusCd]="c.statusCd">{{ c.statusCd }}</app-status-badge></td>
              <td>
                <button type="button" class="btn secondary btn-compact" (click)="deactivateCode(c)" *ngIf="c.statusCd === 'active'">
                  Deactivate
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="ws-empty" *ngIf="!loadingCodes() && !codeValues().length">No code values on this page.</p>

      <div class="pager" *ngIf="!loadingCodes() && codeTotal() > 0">
        <span class="pager-meta">{{ codeRangeLabel() }} · Page {{ codePage() }} / {{ codeTotalPages() }}</span>
        <div class="actions">
          <button type="button" class="btn secondary" [disabled]="codePage() <= 1" (click)="goCodeFirst()">First</button>
          <button type="button" class="btn secondary" [disabled]="codePage() <= 1" (click)="goCodePrev()">Previous</button>
          <button type="button" class="btn secondary" [disabled]="codePage() >= codeTotalPages()" (click)="goCodeNext()">Next</button>
          <button type="button" class="btn secondary" [disabled]="codePage() >= codeTotalPages()" (click)="goCodeLast()">Last</button>
        </div>
      </div>
    </div>
  </section>
</div>

<div class="admin-overlay" *ngIf="hierarchyModalOpen()" (click)="closeHierarchyModal()">
  <div class="admin-modal" role="dialog" aria-modal="true" aria-labelledby="adminHierarchyTitle" (click)="$event.stopPropagation()">
    <header class="admin-modal-head">
      <h3 id="adminHierarchyTitle">Add hierarchy node</h3>
      <button type="button" class="admin-modal-close" (click)="closeHierarchyModal()" aria-label="Close">×</button>
    </header>
    <div class="admin-modal-body">
      <div class="form-grid">
        <label>Level <select [(ngModel)]="hierarchyForm.level"><option value="category">category</option><option value="subtype">subtype</option></select></label>
        <label *ngIf="hierarchyForm.level === 'subtype'">Parent category
          <select [(ngModel)]="hierarchyForm.parentId">
            <option value="">— select —</option>
            <option *ngFor="let c of categories()" [value]="c.id">{{ c.name }}</option>
          </select>
        </label>
        <label>Code <input [(ngModel)]="hierarchyForm.code" /></label>
        <label>Name <input [(ngModel)]="hierarchyForm.name" /></label>
        <label>Sort <input [(ngModel)]="hierarchyForm.sortOrder" type="number" /></label>
      </div>
    </div>
    <footer class="admin-modal-foot">
      <button type="button" class="btn secondary" (click)="closeHierarchyModal()">Cancel</button>
      <button type="button" class="btn primary" (click)="createHierarchy()">Save hierarchy node</button>
    </footer>
  </div>
</div>

<div class="admin-overlay" *ngIf="codeModalOpen()" (click)="closeCodeModal()">
  <div class="admin-modal" role="dialog" aria-modal="true" aria-labelledby="adminCodeTitle" (click)="$event.stopPropagation()">
    <header class="admin-modal-head">
      <h3 id="adminCodeTitle">Add code value</h3>
      <button type="button" class="admin-modal-close" (click)="closeCodeModal()" aria-label="Close">×</button>
    </header>
    <div class="admin-modal-body">
      <div class="form-grid">
        <label>Code type <input [(ngModel)]="codeForm.codeType" /></label>
        <label>Code <input [(ngModel)]="codeForm.code" /></label>
        <label>Label <input [(ngModel)]="codeForm.label" /></label>
        <label>Sort <input [(ngModel)]="codeForm.sortOrder" type="number" /></label>
      </div>
    </div>
    <footer class="admin-modal-foot">
      <button type="button" class="btn secondary" (click)="closeCodeModal()">Cancel</button>
      <button type="button" class="btn primary" (click)="createCodeValue()">Save code value</button>
    </footer>
  </div>
</div>

<div class="toast" *ngIf="toast()">{{ toast() }}</div>
